import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import api from '@/lib/api';
import type { EngineUpdateStatus } from '@/types/api';

const LS_KEY = 'engine-update-triggered-at';
const MIN_UPDATE_DURATION_MS = 60_000;

/**
 * Check if an engine update is in the forced loading window.
 * Uses localStorage so it survives page refreshes.
 */
function getUpdateTriggeredAt(): number | null {
  const raw = localStorage.getItem(LS_KEY);
  if (!raw) return null;
  const ts = parseInt(raw, 10);
  if (isNaN(ts)) return null;
  // Expired — clean up
  if (Date.now() - ts > MIN_UPDATE_DURATION_MS) {
    localStorage.removeItem(LS_KEY);
    return null;
  }
  return ts;
}

export function useEngineUpdates() {
  return useQuery<EngineUpdateStatus>({
    queryKey: ['engine-updates'],
    queryFn: async () => {
      const res = await api.get('/engine/updates');
      return res.data;
    },
    refetchOnMount: 'always',
    refetchInterval: () => {
      // Poll faster while within the update window
      if (getUpdateTriggeredAt()) return 5_000;
      return 30_000;
    },
  });
}

/**
 * Returns { mutate, isUpdating, updateFailed, secondsRemaining }.
 * isUpdating is true while within 60s of the trigger OR while the API reports updateInProgress.
 * updateFailed is true if the worker never picked up the update request (offline/crashed).
 * Survives page refreshes via localStorage.
 */
export function useApplyEngineUpdate(status?: EngineUpdateStatus) {
  const qc = useQueryClient();
  const [now, setNow] = useState(Date.now());

  const triggeredAt = getUpdateTriggeredAt();
  const inWindow = triggeredAt != null && (now - triggeredAt) < MIN_UPDATE_DURATION_MS;
  const isUpdating = (inWindow || !!status?.updateInProgress) && !status?.updateFailed;
  const secondsRemaining = triggeredAt
    ? Math.max(0, Math.ceil((MIN_UPDATE_DURATION_MS - (now - triggeredAt)) / 1000))
    : 0;

  // Tick every second while in the update window
  useEffect(() => {
    if (!isUpdating) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isUpdating]);

  // Clear localStorage when update completes or fails
  useEffect(() => {
    if (triggeredAt && (status?.updateFailed || (!inWindow && !status?.updateInProgress))) {
      localStorage.removeItem(LS_KEY);
    }
  }, [inWindow, status?.updateInProgress, status?.updateFailed, triggeredAt]);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/engine/update');
      return res.data;
    },
    onMutate: () => {
      localStorage.setItem(LS_KEY, String(Date.now()));
      setNow(Date.now());
      qc.setQueryData<EngineUpdateStatus>(['engine-updates'], (old) =>
        old ? { ...old, updateInProgress: true, updateFailed: false } : old,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['engine-updates'] });
    },
  });

  return {
    mutate: mutation.mutate,
    isPending: mutation.isPending,
    isUpdating,
    updateFailed: !!status?.updateFailed,
    allWorkersOffline: !!status?.allWorkersOffline,
    secondsRemaining,
  };
}
